import { Box, Link, Stack, styled } from '@mui/material'
import { ReactNode } from 'react'
import { Footer as EinwertFooter } from 'ComponentLibrary/src/molecules/Footer'
import { Typography } from 'ComponentLibrary/src/piazza'
import { tokens } from 'ComponentLibrary/style-dictionary/output'

export const DEFAULT_AUTH_BACKGROUND_POSITION = 'center 88%'

type AuthenticationHeaderProps = {
    authLogoSrc: string
    authBackgroundSrc?: string
    authBackgroundPosition?: string
    authSubtitle: string
}

export function AuthenticationHeader({
    authLogoSrc,
    authBackgroundSrc,
    authBackgroundPosition = DEFAULT_AUTH_BACKGROUND_POSITION,
    authSubtitle,
}: AuthenticationHeaderProps) {
    return (
        <>
            {authBackgroundSrc && (
                <HeroBackground>
                    <HeroBackgroundImage
                        src={authBackgroundSrc}
                        alt=""
                        aria-hidden="true"
                        style={{ objectPosition: authBackgroundPosition }}
                    />
                    <HeroBackgroundOverlay />
                </HeroBackground>
            )}
            <HeroBrand>
                <HeroLogo src={authLogoSrc} alt="einwert" />
                {authBackgroundSrc && (
                    <Typography variant="headline1" color={tokens.ColorTextInverse}>
                        {authSubtitle}
                    </Typography>
                )}
            </HeroBrand>
        </>
    )
}

export function AuthenticationFooter({ showCookieSettings }: { showCookieSettings?: boolean } = {}) {
    return (
        <FooterBar>
            <EinwertFooter showCookieSettings={showCookieSettings} />
        </FooterBar>
    )
}

export function AuthenticationSupportNote({
    prefix,
    suffix,
    email = 'support@einwert.com',
}: {
    prefix: string
    suffix?: string
    email?: string
}) {
    const suffixSpacing = suffix && !['.', ',', ';', ':', '!', '?'].includes(suffix[0]) ? ' ' : ''

    return (
        <Typography variant="textXS" color={tokens.ColorTextSubtlest}>
            {prefix}{' '}
            <Link href={`mailto:${email}`} color={tokens.ColorTextLinkDefault}>
                {email}
            </Link>
            {suffix && `${suffixSpacing}${suffix}`}
        </Typography>
    )
}

type AuthenticationPageLayoutProps = AuthenticationHeaderProps & {
    children: ReactNode
    showCookieSettings?: boolean
}

export function AuthenticationPageLayout({ children, showCookieSettings, ...headerProps }: AuthenticationPageLayoutProps) {
    return (
        <Page>
            <Content>
                <AuthenticationHeader {...headerProps} />
                <Card>{children}</Card>
            </Content>
            <AuthenticationFooter showCookieSettings={showCookieSettings} />
        </Page>
    )
}

const Page = styled(Box)({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '100dvh',
    overflow: 'hidden',
    padding: `${tokens.SizingSpacing400} ${tokens.SizingSpacing200} calc(${tokens.SizingSpacing500} + ${tokens.SizingSpacing200})`,
})

const Content = styled(Stack)({
    alignItems: 'stretch',
    maxWidth: '478px',
    width: '100%',
})

const Card = styled(Stack)({
    backgroundColor: tokens.ColorSurfaceDefault,
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerL,
    boxShadow: tokens.Popup.boxShadow,
    padding: tokens.SizingSpacing400,
    position: 'relative',
    zIndex: 1,
})

const HeroBackground = styled(Box)({
    inset: 0,
    overflow: 'hidden',
    position: 'fixed',
    zIndex: -1,
})

const HeroBackgroundImage = styled('img')({
    height: '100%',
    inset: 0,
    objectFit: 'cover',
    objectPosition: DEFAULT_AUTH_BACKGROUND_POSITION,
    position: 'absolute',
    transform: 'scale(1.08)',
    transformOrigin: 'center center',
    width: '100%',
})

const HeroBackgroundOverlay = styled(Box)({
    background: tokens.ColorBackgroundOverlay,
    inset: 0,
    position: 'absolute',
})

const HeroBrand = styled(Stack)({
    alignItems: 'center',
    gap: tokens.SizingSpacing150,
    marginBottom: tokens.SizingSpacing400,
    position: 'relative',
    textAlign: 'center',
    zIndex: 1,
})

const HeroLogo = styled('img')({
    display: 'block',
    height: tokens.SizingSpacing500,
    width: 'auto',
})

const FooterBar = styled(Box)({
    backgroundColor: tokens.ColorGraphSequentialOneDarkest,
    bottom: 0,
    left: 0,
    padding: `${tokens.SizingSpacing100} ${tokens.SizingSpacingNone}`,
    position: 'fixed',
    width: '100%',
    zIndex: 2,
    '& a': {
        color: tokens.ColorTextInverse,
    },
})
